import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import type { ReactNode } from 'react';
import { metricsReadQuery } from '../api/metrics.js';
import HourlyMetricGraph from '../components/MetricsGraph.js';
import { FRONTEND_BUILD_STRING, FRONTEND_SERVICE_URL } from '../settings.js';

interface AnalyzerTelemetrySnapshot {
  day: string;
  metrics: Record<string, number>;
}

interface TestRunStatusCount {
  status: string;
  count: number;
}

function formatMetricValue(value: number) {
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }

  return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

interface DashboardSectionProps {
  title: string;
  children: ReactNode;
  accentColor: string;
}

function DashboardSection({ title, children, accentColor }: DashboardSectionProps) {
  const borderColor = `${accentColor}55`;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor,
        height: '100%',
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor,
          background: `linear-gradient(90deg, ${accentColor}22 0%, transparent 100%)`
        }}
      >
        <Typography variant="h6" sx={{ color: accentColor, fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 2, flexGrow: 1 }}>{children}</Box>
    </Paper>
  );
}

export function MainView() {
  const { data: metrics, isLoading, isError, error } = metricsReadQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography color="error" variant="h6">
          Error: {error.message}
        </Typography>
      </Box>
    );
  }

  if (!metrics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography color="text.secondary" variant="body1">
          No metrics available.
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {/* Overview */}
        <Grid item xs={12}>
          <DashboardSection title="Overview" accentColor="#1565c0">
            <Typography variant="body1">Service URL: {FRONTEND_SERVICE_URL}</Typography>
            <Typography variant="body1">Build String: {FRONTEND_BUILD_STRING}</Typography>
          </DashboardSection>
        </Grid>
        {/* Telemetry */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <DashboardSection title="Telemetry" accentColor="#00897b">
            {Array.isArray(metrics.telemetry) && metrics.telemetry.length > 0 ? (
              metrics.telemetry.map((snapshot: AnalyzerTelemetrySnapshot) => {
                const rows = Object.entries(snapshot.metrics).sort(([left], [right]) =>
                  left.localeCompare(right)
                );

                return (
                  <Paper key={snapshot.day} variant="outlined" sx={{ mb: 2, borderColor: '#00897b55' }}>
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid',
                        borderColor: '#00897b55',
                        background: 'linear-gradient(90deg, #00897b14 0%, transparent 100%)'
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ color: '#00695c', fontWeight: 600 }}>
                        {snapshot.day}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rows.length} metrics
                      </Typography>
                    </Box>

                    <TableContainer sx={{ maxHeight: 280 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Metric</TableCell>
                            <TableCell align="right">Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(([name, value]) => (
                            <TableRow key={`${snapshot.day}:${name}`} hover>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {name}
                              </TableCell>
                              <TableCell align="right">{formatMetricValue(value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                );
              })
            ) : (
              <Typography variant="body2">No telemetry available.</Typography>
            )}
          </DashboardSection>
        </Grid>
        {/* Test Runs */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <DashboardSection title="Test Runs (1h)" accentColor="#6a1b9a">
            {Array.isArray(metrics.runs) && metrics.runs.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Runs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(metrics.runs as TestRunStatusCount[]).map((row) => (
                      <TableRow key={row.status} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.status}</TableCell>
                        <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2">No test run metrics available for the last 24 hours.</Typography>
            )}
          </DashboardSection>
        </Grid>
        {/* Snapshots */}
        <Grid item xs={12}>
          <DashboardSection title="Snapshots" accentColor="#ef6c00">
            <HourlyMetricGraph data={metrics.snapshots} metricName="Snapshots" lineColor="#8884d8" />
          </DashboardSection>
        </Grid>
      </Grid>
    </Container>
  );
}
